/**
 * User menu — avatar trigger with a link to your profile and sign-out. Settings
 * intentionally live in the org/tenant menu, not here (this stays personal:
 * profile + session).
 */
import { Link } from "@tanstack/react-router";
import { ChevronDown, LogOut, User } from "lucide-react";
import { useSession } from "@authe/ui-core/session";
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@authe/ui-core";
import { ROUTES } from "@/config";

export function ProfileMenu(): React.ReactElement {
  const { session, logout } = useSession();
  const user = session?.user;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 px-2">
          <Avatar
            size="sm"
            src={user?.avatar ?? undefined}
            alt={user?.name ?? "User"}
            fallback={(user?.name ?? user?.email ?? "U").slice(0, 1).toUpperCase()}
          />
          <span className="hidden max-w-[10rem] truncate text-sm md:inline">
            {user?.name ?? user?.email ?? "Account"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="truncate">{user?.name ?? "Signed in"}</div>
          <div className="truncate text-xs font-normal text-muted-foreground">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={ROUTES.profile}>
            <User className="h-4 w-4" />
            Your profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() =>
            // Land on the same-origin login SPA's signed-out view (`/login`,
            // served by the login app, so it's OUTSIDE `RequireSession` and
            // won't auto re-login). We deliberately do NOT pass `federated`:
            // that would also end the user's upstream Google/IdP session
            // (Gmail, etc.), which is more than a dashboard sign-out should do.
            // Silent re-login is already prevented by the signed-out view (no
            // auto-login) + the login SPA's `prompt=login` (interactive re-auth).
            void logout({
              returnTo: `${window.location.origin}/login?loggedout=1`,
            })
          }
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
